import { useState, useMemo } from "react";
import { useGetKnowledgeItem } from "@/lib/api/knowledge";
import { KnowledgeEditor, useKnowledgeEditor } from "@/section/knowledge/components/KnowledgeEditor";
import { Loader } from "@/components/ui/loader";
import { Combobox } from "@/components/ui/combobox";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const GENERAL_CONTRACT_LANGUAGE_ID = "c566c75b-d6b2-48e2-b203-78eea1c1e6d6";
const OTC_CONTRACT_LANGUAGE_ID = "fc4878db-1b1a-4b74-addb-ee5304fbb2a8";

type ContractType = "general" | "otc";

interface TiptapNode {
  type: string;
  attrs?: { level?: number; [key: string]: unknown };
  content?: TiptapNode[];
  text?: string;
}

interface TiptapDoc {
  type: "doc";
  content: TiptapNode[];
}

function extractTextFromNode(node: TiptapNode): string {
  if (node.text) return node.text;
  if (!node.content) return "";
  return node.content.slice(0,1).map(extractTextFromNode).join("");
}

interface Section {
  label: string;
  nodes: TiptapNode[];
}

function parseSections(contentJson: string): Section[] {
  let doc: TiptapDoc;
  try {
    doc = JSON.parse(contentJson);
  } catch {
    return [];
  }

  if (!doc?.content?.length) return [];

  const sections: Section[] = [];
  let currentSection: Section | null = null;

  for (const node of doc.content) {
    if (node.type === "heading" && node.attrs?.level === 2) {
      if (currentSection) {
        sections.push(currentSection);
      }
      currentSection = {
        label: extractTextFromNode(node),
        nodes: [node],
      };
    } else {
      if (currentSection) {
        currentSection.nodes.push(node);
      }
    }
  }

  if (currentSection) {
    sections.push(currentSection);
  }

  return sections;
}

const PINNED_SECTION_PATTERNS = [
  /early exit of otc contract/i,
  /otc contract amendments/i,
  /final futures pricing/i,
];

function isPinnedSection(label: string): boolean {
  return PINNED_SECTION_PATTERNS.some((pattern) => pattern.test(label));
}

function buildDocFromNodes(nodes: TiptapNode[]): string {
  return JSON.stringify({ type: "doc", content: nodes });
}

interface SectionViewerProps {
  knowledgeItemId: string;
  title: string;
}

function SectionViewer({ knowledgeItemId, title }: SectionViewerProps) {
  const knowledgeItemResult = useGetKnowledgeItem(knowledgeItemId);
  const knowledgeItem = knowledgeItemResult?.data?.data;

  const sections = useMemo(() => {
    if (!knowledgeItem?.content) return [];
    return parseSections(knowledgeItem.content);
  }, [knowledgeItem?.content]);

  const [selectedSection, setSelectedSection] = useState<string>("");

  const sectionOptions = useMemo(
    () => sections.filter(item => !isPinnedSection(item.label)).map((s) => ({ label: s.label, value: s.label })),
    [sections]
  );

  const activeSection = useMemo(() => {
    if (!selectedSection) return sections[0] ?? null;
    return sections.find((s) => s.label === selectedSection) ?? sections[0] ?? null;
  }, [selectedSection, sections]);

  const pinnedSections = useMemo(
    () => sections.filter((s) => isPinnedSection(s.label)),
    [sections]
  );

  const activeSectionContent = useMemo(() => {
    if (!activeSection) return undefined;
    return buildDocFromNodes(activeSection.nodes);
  }, [activeSection]);

  const pinnedSectionsContent = useMemo(() => {
    const nodes = pinnedSections
      .filter((s) => s.label !== activeSection?.label)
      .flatMap((s) => s.nodes);
    if (!nodes.length) return undefined;
    return buildDocFromNodes(nodes);
  }, [pinnedSections, activeSection]);

  const { editor, error, handleFileUpload, fileInputRef } = useKnowledgeEditor({
    mode: "view",
    existingData: activeSectionContent,
    taskId: knowledgeItemId,
  });

  const { editor: pinnedEditor, error: pinnedError, handleFileUpload: pinnedHandleFileUpload, fileInputRef: pinnedFileInputRef } = useKnowledgeEditor({
    mode: "view",
    existingData: pinnedSectionsContent,
    taskId: knowledgeItemId,
  });

  if (knowledgeItemResult.isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader className="w-8 h-8" />
      </div>
    );
  }

  if (knowledgeItemResult.isError || !knowledgeItem) {
    return (
      <div className="flex items-center justify-center h-32">
        <p className="text-destructive">Failed to load {title}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full overflow-hidden">
      <div className="w-72">
        <Combobox
          className="w-[400px] md:w-[500px]"
          popoverClassName="w-[400px] md:w-[500px]"
          dwimOnChange
          placeholder="Select a section..."
          options={sectionOptions}
          value={selectedSection || (sections[0]?.label ?? "")}
          onValueChange={(val) => setSelectedSection(val)}
        />
      </div>
      <div className="flex-1 overflow-auto flex flex-col gap-4">
        <KnowledgeEditor
          mode="view"
          existingData={activeSectionContent}
          taskId={knowledgeItemId}
          editor={editor}
          handleFileUpload={handleFileUpload}
          fileInputRef={fileInputRef}
          error={error}
        />
        {pinnedSectionsContent && (
          <>
            <hr className="border-border" />
            <KnowledgeEditor
              mode="view"
              existingData={pinnedSectionsContent}
              taskId={knowledgeItemId}
              editor={pinnedEditor}
              handleFileUpload={pinnedHandleFileUpload}
              fileInputRef={pinnedFileInputRef}
              error={pinnedError}
            />
          </>
        )}
      </div>
    </div>
  );
}

export function ContractsLanguageDashboard() {
  const [activeType, setActiveType] = useState<ContractType>("general");

  return (
    <div className="flex flex-col gap-4 h-full overflow-hidden">
      <div className="flex flex-row gap-2 items-center">
        <Button
          variant="ghost"
          className={cn(
            "text-base font-semibold p-0 h-auto hover:bg-transparent",
            activeType === "general"
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setActiveType("general")}
        >
          General
        </Button>
        <span className="text-base font-semibold text-muted-foreground">/</span>
        <Button
          variant="ghost"
          className={cn(
            "text-base font-semibold p-0 h-auto hover:bg-transparent",
            activeType === "otc"
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setActiveType("otc")}
        >
          OTC
        </Button>
      </div>

      {activeType === "general" && (
        <SectionViewer
          key="general"
          knowledgeItemId={GENERAL_CONTRACT_LANGUAGE_ID}
          title="General Contract Language"
        />
      )}
      {activeType === "otc" && (
        <SectionViewer
          key="otc"
          knowledgeItemId={OTC_CONTRACT_LANGUAGE_ID}
          title="OTC Contract Language"
        />
      )}
    </div>
  );
}
